import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../components/ui/Toast";

function PermessiRuoliPage() {
  const { apiRequest } = useAuth();
  const { showToast } = useToast();

  // Section permissions state
  const [permissions, setPermissions] = useState(null);
  const [originalPermissions, setOriginalPermissions] = useState(null);
  const [sections, setSections] = useState([]);
  const [roles, setRoles] = useState([]);
  const [defaults, setDefaults] = useState(null);

  // Action permissions state
  const [actionPermissions, setActionPermissions] = useState(null);
  const [originalActionPermissions, setOriginalActionPermissions] =
    useState(null);
  const [actions, setActions] = useState([]);
  const [actionDefaults, setActionDefaults] = useState(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [sectionData, actionData] = await Promise.all([
        apiRequest("/admin/permessi-ruoli"),
        apiRequest("/admin/permessi-azioni"),
      ]);

      setPermissions(sectionData.permissions);
      setOriginalPermissions(
        JSON.parse(JSON.stringify(sectionData.permissions)),
      );
      setSections(sectionData.sections);
      setRoles(sectionData.roles);
      setDefaults(sectionData.defaults);

      setActionPermissions(actionData.actionPermissions);
      setOriginalActionPermissions(
        JSON.parse(JSON.stringify(actionData.actionPermissions)),
      );
      setActions(actionData.actions);
      setActionDefaults(actionData.defaults);
    } catch (err) {
      setError("Errore nel caricamento dei permessi");
      showToast("Errore nel caricamento dei permessi", "error");
    } finally {
      setLoading(false);
    }
  }, [apiRequest, showToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Change detection
  const hasSectionChanges =
    permissions &&
    originalPermissions &&
    JSON.stringify(permissions) !== JSON.stringify(originalPermissions);

  const hasActionChanges =
    actionPermissions &&
    originalActionPermissions &&
    JSON.stringify(actionPermissions) !==
      JSON.stringify(originalActionPermissions);

  const hasChanges = hasSectionChanges || hasActionChanges;

  // Section permission helpers
  const isChecked = (roleId, sectionId) => {
    return permissions?.[roleId]?.includes(sectionId) ?? false;
  };

  const isDefault = (roleId, sectionId) => {
    return defaults?.[roleId]?.includes(sectionId) ?? false;
  };

  const isChanged = (roleId, sectionId) => {
    const current = permissions?.[roleId]?.includes(sectionId) ?? false;
    const original =
      originalPermissions?.[roleId]?.includes(sectionId) ?? false;
    return current !== original;
  };

  const togglePermission = (roleId, sectionId) => {
    setPermissions((prev) => {
      const updated = { ...prev };
      const roleSections = [...(updated[roleId] || [])];

      if (roleSections.includes(sectionId)) {
        updated[roleId] = roleSections.filter((s) => s !== sectionId);
      } else {
        roleSections.push(sectionId);
        updated[roleId] = roleSections;
      }

      return updated;
    });
  };

  // Action permission helpers
  const isActionChecked = (roleId, actionId) => {
    return actionPermissions?.[roleId]?.includes(actionId) ?? false;
  };

  const isActionDefault = (roleId, actionId) => {
    return actionDefaults?.[roleId]?.includes(actionId) ?? false;
  };

  const isActionChanged = (roleId, actionId) => {
    const current = actionPermissions?.[roleId]?.includes(actionId) ?? false;
    const original =
      originalActionPermissions?.[roleId]?.includes(actionId) ?? false;
    return current !== original;
  };

  const toggleActionPermission = (roleId, actionId) => {
    setActionPermissions((prev) => {
      const updated = { ...prev };
      const roleActions = [...(updated[roleId] || [])];

      if (roleActions.includes(actionId)) {
        updated[roleId] = roleActions.filter((a) => a !== actionId);
      } else {
        roleActions.push(actionId);
        updated[roleId] = roleActions;
      }

      return updated;
    });
  };

  // Save both section and action permissions
  const handleSave = async () => {
    setSaving(true);
    try {
      const promises = [];
      if (hasSectionChanges) {
        promises.push(
          apiRequest("/admin/permessi-ruoli", {
            method: "PUT",
            body: JSON.stringify({ permissions }),
          }),
        );
      }
      if (hasActionChanges) {
        promises.push(
          apiRequest("/admin/permessi-azioni", {
            method: "PUT",
            body: JSON.stringify({ actionPermissions }),
          }),
        );
      }
      await Promise.all(promises);
      setOriginalPermissions(JSON.parse(JSON.stringify(permissions)));
      setOriginalActionPermissions(
        JSON.parse(JSON.stringify(actionPermissions)),
      );
      showToast("Permessi salvati con successo", "success");
    } catch (err) {
      showToast(err.message || "Errore nel salvataggio", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!confirm("Ripristinare tutti i permessi ai valori predefiniti?"))
      return;

    setSaving(true);
    try {
      const [sectionData, actionData] = await Promise.all([
        apiRequest("/admin/permessi-ruoli/reset", { method: "POST" }),
        apiRequest("/admin/permessi-azioni/reset", { method: "POST" }),
      ]);
      setPermissions(sectionData.permissions);
      setOriginalPermissions(
        JSON.parse(JSON.stringify(sectionData.permissions)),
      );
      setActionPermissions(actionData.actionPermissions);
      setOriginalActionPermissions(
        JSON.parse(JSON.stringify(actionData.actionPermissions)),
      );
      showToast("Permessi ripristinati ai valori predefiniti", "success");
    } catch (err) {
      showToast("Errore nel ripristino", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setPermissions(JSON.parse(JSON.stringify(originalPermissions)));
    setActionPermissions(JSON.parse(JSON.stringify(originalActionPermissions)));
  };

  // Seleziona/deseleziona tutta la riga (sezione)
  const toggleRow = (sectionId) => {
    const allChecked = roles.every((role) =>
      isChecked(String(role.id), sectionId),
    );
    setPermissions((prev) => {
      const updated = { ...prev };
      for (const role of roles) {
        const key = String(role.id);
        const roleSections = [...(updated[key] || [])];
        if (allChecked) {
          updated[key] = roleSections.filter((s) => s !== sectionId);
        } else if (!roleSections.includes(sectionId)) {
          roleSections.push(sectionId);
          updated[key] = roleSections;
        }
      }
      return updated;
    });
  };

  // Seleziona/deseleziona tutta la colonna (ruolo)
  const toggleColumn = (roleId) => {
    const key = String(roleId);
    const allChecked = sections.every((s) => isChecked(key, s.id));
    setPermissions((prev) => {
      const updated = { ...prev };
      if (allChecked) {
        updated[key] = [];
      } else {
        updated[key] = sections.map((s) => s.id);
      }
      return updated;
    });
  };

  // Seleziona/deseleziona riga azione
  const toggleActionRow = (actionId) => {
    const allChecked = roles.every((role) =>
      isActionChecked(String(role.id), actionId),
    );
    setActionPermissions((prev) => {
      const updated = { ...prev };
      for (const role of roles) {
        const key = String(role.id);
        const roleActions = [...(updated[key] || [])];
        if (allChecked) {
          updated[key] = roleActions.filter((a) => a !== actionId);
        } else if (!roleActions.includes(actionId)) {
          roleActions.push(actionId);
          updated[key] = roleActions;
        }
      }
      return updated;
    });
  };

  // Seleziona/deseleziona colonna azione (ruolo)
  const toggleActionColumn = (roleId) => {
    const key = String(roleId);
    const allChecked = actions.every((a) => isActionChecked(key, a.id));
    setActionPermissions((prev) => {
      const updated = { ...prev };
      if (allChecked) {
        updated[key] = [];
      } else {
        updated[key] = actions.map((a) => a.id);
      }
      return updated;
    });
  };

  // Shared checkbox cell renderer
  const CheckboxCell = ({ checked, changed, defaultVal, onChange }) => (
    <div className="flex items-center justify-center">
      <label
        className={`relative inline-flex items-center justify-center w-8 h-8 rounded cursor-pointer transition-all
          ${changed ? "ring-2 ring-amber-400 ring-offset-1" : ""}
          ${checked ? "bg-primary-100 hover:bg-primary-200" : "bg-gray-100 hover:bg-gray-200"}
        `}
        title={
          changed
            ? `Modificato (default: ${defaultVal ? "attivo" : "disattivo"})`
            : checked
              ? "Attivo"
              : "Disattivo"
        }
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          className="sr-only"
        />
        {checked ? (
          <svg
            className="w-5 h-5 text-primary-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2.5}
              d="M5 13l4 4L19 7"
            />
          </svg>
        ) : (
          <svg
            className="w-4 h-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        )}
      </label>
    </div>
  );

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="spinner w-10 h-10"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 text-red-600 p-4 rounded-lg">
          {error}
          <button onClick={fetchData} className="ml-4 underline">
            Riprova
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Gestione Permessi Ruoli
          </h1>
          <p className="text-gray-500 mt-1">
            Configura la visibilit&agrave; delle sezioni e le azioni permesse
            per ogni ruolo
          </p>
        </div>
        <button
          onClick={handleReset}
          disabled={saving}
          className="btn-secondary text-sm"
        >
          Ripristina Default
        </button>
      </div>

      {/* Info Alert */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <span className="text-blue-500 text-lg mt-0.5">ℹ</span>
        <div className="text-sm text-blue-700">
          <p className="font-medium">
            Il ruolo Admin ha sempre accesso completo a tutte le sezioni e
            azioni.
          </p>
          <p className="mt-1">
            Le modifiche si applicano al prossimo login degli utenti o dopo il
            refresh della pagina.
          </p>
        </div>
      </div>

      {/* Section Permissions Matrix */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b">
          <h2 className="text-lg font-semibold text-gray-800">
            Visibilit&agrave; Sezioni
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Controlla quali sezioni del portale sono visibili per ogni ruolo
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700 min-w-[200px]">
                  Sezione
                </th>
                {roles.map((role) => (
                  <th
                    key={role.id}
                    className="px-3 py-3 text-center min-w-[100px]"
                  >
                    <button
                      onClick={() => toggleColumn(role.id)}
                      className="text-sm font-semibold text-gray-700 hover:text-primary-600 transition-colors"
                      title={`Seleziona/deseleziona tutto per ${role.label}`}
                    >
                      {role.label}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sections.map((section) => (
                <tr
                  key={section.id}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleRow(section.id)}
                      className="flex items-center gap-3 hover:text-primary-600 transition-colors text-left"
                      title={`Seleziona/deseleziona ${section.label} per tutti i ruoli`}
                    >
                      <span className="text-lg">{section.icon}</span>
                      <div>
                        <div className="font-medium text-gray-900">
                          {section.label}
                        </div>
                        <div className="text-xs text-gray-400">
                          {section.description}
                        </div>
                      </div>
                    </button>
                  </td>
                  {roles.map((role) => {
                    const roleKey = String(role.id);
                    return (
                      <td key={role.id} className="px-3 py-3 text-center">
                        <CheckboxCell
                          checked={isChecked(roleKey, section.id)}
                          changed={isChanged(roleKey, section.id)}
                          defaultVal={isDefault(roleKey, section.id)}
                          onChange={() => togglePermission(roleKey, section.id)}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Action Permissions Matrix */}
      {actions.length > 0 && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b">
            <h2 className="text-lg font-semibold text-gray-800">
              Permessi Azioni
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Controlla quali operazioni specifiche ogni ruolo pu&ograve;
              eseguire
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700 min-w-[200px]">
                    Azione
                  </th>
                  {roles.map((role) => (
                    <th
                      key={role.id}
                      className="px-3 py-3 text-center min-w-[100px]"
                    >
                      <button
                        onClick={() => toggleActionColumn(role.id)}
                        className="text-sm font-semibold text-gray-700 hover:text-primary-600 transition-colors"
                        title={`Seleziona/deseleziona tutte le azioni per ${role.label}`}
                      >
                        {role.label}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {actions.map((action) => (
                  <tr
                    key={action.id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleActionRow(action.id)}
                        className="flex items-center gap-3 hover:text-primary-600 transition-colors text-left"
                        title={`Seleziona/deseleziona ${action.label} per tutti i ruoli`}
                      >
                        <span className="text-lg">{action.icon}</span>
                        <div>
                          <div className="font-medium text-gray-900">
                            {action.label}
                          </div>
                          <div className="text-xs text-gray-400">
                            {action.description}
                          </div>
                        </div>
                      </button>
                    </td>
                    {roles.map((role) => {
                      const roleKey = String(role.id);
                      return (
                        <td key={role.id} className="px-3 py-3 text-center">
                          <CheckboxCell
                            checked={isActionChecked(roleKey, action.id)}
                            changed={isActionChanged(roleKey, action.id)}
                            defaultVal={isActionDefault(roleKey, action.id)}
                            onChange={() =>
                              toggleActionPermission(roleKey, action.id)
                            }
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Footer con azioni (sticky) */}
      {hasChanges && (
        <div className="sticky bottom-0 z-10 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-center justify-between shadow-lg">
          <span className="text-sm text-amber-700 font-medium">
            Ci sono modifiche non salvate
          </span>
          <div className="flex gap-3">
            <button
              onClick={handleCancel}
              disabled={saving}
              className="btn-secondary text-sm"
            >
              Annulla
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-primary text-sm"
            >
              {saving ? "Salvataggio..." : "Salva Modifiche"}
            </button>
          </div>
        </div>
      )}

      {/* Legenda */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Legenda</h3>
        <div className="flex flex-wrap gap-6 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded bg-primary-100 flex items-center justify-center">
              <svg
                className="w-5 h-5 text-primary-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <span>Accesso consentito</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center">
              <svg
                className="w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <span>Accesso negato</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded bg-primary-100 ring-2 ring-amber-400 ring-offset-1 flex items-center justify-center">
              <svg
                className="w-5 h-5 text-primary-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <span>Modificato (non salvato)</span>
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-3">
          Clicca sul nome di un ruolo (colonna) o di una sezione/azione (riga)
          per selezionare/deselezionare tutti.
        </p>
      </div>
    </div>
  );
}

export default PermessiRuoliPage;
